/**
 * Hooks de la SUPERFICIE DEL ALUMNO (carriles 1C/1D): su aula y su expediente.
 *
 * No abren ninguna fuente de datos nueva — componen los hooks que ya existen
 * (`use-assessment`, `use-schedule-data`, `use-session-plan`, `use-people-data`)
 * con los selectores puros de `@space/shared`. Todo lo que leen ya viaja al
 * dispositivo del alumno por `my_cohorts_student`, así que el aula y el boletín
 * funcionan igual sin conexión y no hacen falta reglas de sync nuevas.
 */
import { type Accessor, createMemo } from "solid-js";
import {
  attendanceCertification,
  type AttendanceCertification,
  attendanceStatsOf,
  attendanceTimeline,
  type AttendanceTimelineRow,
  buildReportCard,
  type CohortProgressSummary,
  cohortSessionsProgress,
  type CriterionProgressRow,
  directoryRowTitle,
  isPlanPhase,
  type PlanCompletionCtx,
  type PlanModule,
  type ReportCard,
  type ReportCardGrade,
} from "@space/shared";
import { useSession } from "@/lib/auth";
import { useGradeCategories, useGradeItems, useMyGrades } from "./use-assessment";
import { useUserEnrollments } from "./use-people-data";
import { useCohortSessions, useMyAttendance } from "./use-schedule-data";
import {
  useCohortPlanItems,
  useCourseModuleDirectory,
  useMyCourseProgress,
  useMySubmissions,
  useMyWatchProgress,
} from "./use-session-plan";

/** MI matrícula en una cohorte (la fila propia que sincroniza). */
export function useMyEnrollment(cohortId: Accessor<string | undefined>) {
  const { userId } = useSession();
  const enrollments = useUserEnrollments(userId);
  const row = createMemo(() => (enrollments.data ?? []).find((e) => e.cohortId === cohortId()));
  const stats = createMemo(() =>
    attendanceStatsOf(row()?.raw as Record<string, unknown> | undefined),
  );
  return {
    row,
    /** Stats de asistencia denormalizadas por el servidor (rate en 0–100). */
    stats,
    isLoading: () => enrollments.isLoading,
  };
}

/**
 * MI histórico de asistencia en una cohorte + el porcentaje frente al criterio
 * de certificación. El porcentaje NO se recuenta aquí: sale del criterio que
 * evalúa el servidor o, si la cohorte no tiene reglas, de las stats que el
 * propio servidor denormaliza en la matrícula.
 */
export function useMyAttendanceRecord(
  cohortId: Accessor<string | undefined>,
  criteria: Accessor<readonly CriterionProgressRow[]>,
): {
  timeline: Accessor<AttendanceTimelineRow[]>;
  certification: Accessor<AttendanceCertification>;
} {
  const sessions = useCohortSessions(cohortId);
  const records = useMyAttendance();
  const enrollment = useMyEnrollment(cohortId);

  const mine = createMemo(() => (records.data ?? []).filter((r) => r.cohort_id === cohortId()));

  const timeline = createMemo(() => attendanceTimeline(mine(), sessions.data ?? []));
  const certification = createMemo(() => attendanceCertification(enrollment.stats(), criteria()));
  return { timeline, certification };
}

/** Ítems del plan de una cohorte agrupados por clase + contexto de ticks. */
function useCohortPlan(
  cohortId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
) {
  const planItems = useCohortPlanItems(cohortId);
  const directory = useCourseModuleDirectory(courseId);
  const progress = useMyCourseProgress(courseId);
  const submissions = useMySubmissions();
  const watch = useMyWatchProgress();

  const dirMap = createMemo(() => {
    const map = new Map<
      string,
      { kind: string | null; ref_id: string | null; title: string | null }
    >();
    for (const r of directory.data ?? []) {
      map.set(r.id, { kind: r.kind, ref_id: r.ref_id, title: directoryRowTitle(r) });
    }
    return map;
  });

  const bySession = createMemo(() => {
    const dir = dirMap();
    const out = new Map<string, PlanModule[]>();
    for (const sm of planItems.data ?? []) {
      const sessionId = sm.class_session_id;
      if (!sessionId) {
        continue;
      }
      const d = dir.get(sm.module_id ?? "");
      const list = out.get(sessionId) ?? [];
      list.push({
        id: sm.id,
        moduleId: sm.module_id ?? "",
        phase: isPlanPhase(sm.phase) ? sm.phase : "pre",
        position: sm.position ?? 0,
        kind: d?.kind ?? null,
        refId: d?.ref_id ?? null,
        title: d?.title ?? null,
      });
      out.set(sessionId, list);
    }
    return out;
  });

  const ctx = createMemo<PlanCompletionCtx>(() => {
    const lessonStatusByRef = new Map<string, string | null>();
    for (const p of progress.data ?? []) {
      if (p.lesson_id) {
        lessonStatusByRef.set(p.lesson_id, p.status);
      }
    }
    const submittedByAssignment = new Map<string, boolean>();
    for (const s of submissions.data ?? []) {
      if (s.assignment_id) {
        submittedByAssignment.set(s.assignment_id, true);
      }
    }
    const videoDoneByRef = new Map<string, boolean>();
    for (const w of watch.data ?? []) {
      if (w.video_id) {
        videoDoneByRef.set(w.video_id, Boolean(w.completed_at));
      }
    }
    return { lessonStatusByRef, submittedByAssignment, videoDoneByRef };
  });

  return { bySession, ctx, lessonProgress: progress };
}

/**
 * Rejilla «mis N clases» de una cohorte (D1): por clase, si asistí y cuánto del
 * plan llevo hecho. El conteo por clase lo hace `planProgress` (session-plan) —
 * la misma función que pinta los ticks de la vista de plan de clase.
 */
export function useMyCohortProgress(
  cohortId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
): Accessor<CohortProgressSummary> {
  const sessions = useCohortSessions(cohortId);
  const records = useMyAttendance();
  const plan = useCohortPlan(cohortId, courseId);

  const summary = createMemo(() =>
    cohortSessionsProgress(
      sessions.data ?? [],
      plan.bySession(),
      plan.ctx(),
      (records.data ?? []).filter((r) => r.cohort_id === cohortId()),
    ),
  );
  return summary;
}

/**
 * MI boletín de una cohorte (E2): categorías, pesos, mis notas, la final oficial
 * y la proyectada. `grade_category`/`grade_item` sincronizan a toda la cohorte
 * (transparencia de pesos) pero `assignment_grade` solo trae MIS filas, así que
 * de aquí no puede salir la nota de nadie más.
 */
export function useMyReportCard(
  cohortId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
): Accessor<ReportCard> {
  const { userId } = useSession();
  const categories = useGradeCategories(cohortId);
  const items = useGradeItems(cohortId);
  const grades = useMyGrades(userId);
  const enrollment = useMyEnrollment(cohortId);
  const lessonProgress = useMyCourseProgress(courseId);

  const gradeByAssignment = createMemo(() => {
    const map = new Map<string, ReportCardGrade>();
    for (const g of grades.data ?? []) {
      if (g.assignment_id && g.cohort_id === cohortId()) {
        map.set(g.assignment_id, { score: g.score ?? null, status: g.status ?? null });
      }
    }
    return map;
  });

  // Media de las lecciones ya completadas (misma fuente que `auto_blocks` en el
  // servidor); `null` mientras no haya ninguna, para que el ítem cuente como
  // PENDIENTE en la proyección en vez de como un cero.
  const autoBlocksFraction = createMemo(() => {
    const scores = (lessonProgress.data ?? [])
      .filter((p) => p.status === "completed")
      .map((p) => p.score ?? 0);
    return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  });

  const attendanceFraction = createMemo(() => {
    const { rate } = enrollment.stats();
    return rate === null ? null : rate / 100;
  });

  const card = createMemo(() =>
    buildReportCard(categories.data ?? [], items.data ?? [], {
      gradeByAssignment: gradeByAssignment(),
      autoBlocksFraction: autoBlocksFraction(),
      attendanceFraction: attendanceFraction(),
    }),
  );
  return card;
}
